<template>
  <div class="ngd-root">

    <!-- ═══════════════════════════════════════════
         PENDING BANNER
    ══════════════════════════════════════════════ -->
    <transition name="fade-slide">
      <div v-if="groupPending" class="ngd-pending-banner">
        <div class="ngd-pending-inner">
          <div class="ngd-pending-icon">
            <span class="ngd-hourglass">⏳</span>
          </div>
          <div class="ngd-pending-text">
            <strong>{{ njangi_group_name }}</strong> is under review by PettyCash managers.
            Contributions and history are locked until approval — usually 24–48 hours.
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══════════════════════════════════════════
         GROUP HEADER CARD — REDESIGNED
    ══════════════════════════════════════════════ -->
    <div class="ngd-header-card">
      <div class="ngd-header-accent"></div>

      <div class="ngd-header-hero">
        <div class="ngd-header-deco-circle"></div>

        <div class="ngd-header-top">
          <div class="ngd-header-left">
            <div class="ngd-group-avatar">
              <v-icon color="white" size="28">group</v-icon>
            </div>
            <div>
              <div class="ngd-group-name">{{ njangi_group_name }}</div>
              <div class="ngd-group-meta">
                <span class="ngd-cycle-badge" :class="cycleBadgeClass">
                  <v-icon x-small class="mr-1">{{ cycleIcon }}</v-icon>
                  {{ collecting_cycle || 'Loading…' }}
                </span>
                <span v-if="groupPending" class="ngd-status-badge ngd-status-pending">
                  <v-icon x-small class="mr-1">hourglass_empty</v-icon>Pending Approval
                </span>
                <span v-else class="ngd-status-badge ngd-status-active">
                  <v-icon x-small class="mr-1">check_circle</v-icon>Active
                </span>
              </div>
            </div>
          </div>

          <!-- Window indicator -->
          <div v-if="!groupPending && collecting_cycle === 'Weekly'" class="ngd-window-pill" :class="isInWeeklyWindow ? 'ngd-window-open' : 'ngd-window-closed'">
            <v-icon x-small class="mr-1">{{ isInWeeklyWindow ? 'lock_open' : 'lock' }}</v-icon>
            {{ isInWeeklyWindow ? 'Window Open' : 'Window Closed' }}
          </div>
          <div v-if="!groupPending && collecting_cycle === 'Monthly'" class="ngd-window-pill" :class="isInMonthlyWindow ? 'ngd-window-open' : 'ngd-window-closed'">
            <v-icon x-small class="mr-1">{{ isInMonthlyWindow ? 'lock_open' : 'lock' }}</v-icon>
            {{ isInMonthlyWindow ? 'Window Open' : 'Penalty Period' }}
          </div>
        </div>

        <p v-if="njangi_group_description" class="ngd-description" v-html="njangi_group_description"></p>
      </div>

      <div class="ngd-info-grid">
        <div class="ngd-info-item ngd-info-item--contact">
          <div class="ngd-info-accent"></div>
          <span class="ngd-info-label">
            <v-icon x-small class="mr-1">phone</v-icon>Admin Contact
          </span>
          <span class="ngd-info-value">{{ formatPhone(njangi_contact) || '—' }}</span>
        </div>
        <div class="ngd-info-item ngd-info-item--contrib">
          <div class="ngd-info-accent"></div>
          <span class="ngd-info-label">
            <v-icon x-small class="mr-1">payments</v-icon>Contribution
          </span>
          <span class="ngd-info-value">
            {{ formatAmount(contribution_amount) }} <span class="ngd-info-currency">FCFA</span>
          </span>
          <span v-if="collecting_cycle === 'Custom' && customSettings" class="ngd-info-sub">
            / {{ customSettings.interval_value }} {{ customSettings.interval_unit }}
          </span>
          <span v-else-if="collecting_cycle" class="ngd-info-sub ngd-info-sub--cycle">/ {{ collecting_cycle }}</span>
        </div>
        <div v-if="collecting_cycle === 'Weekly'" class="ngd-info-item ngd-info-item--window">
          <div class="ngd-info-accent"></div>
          <span class="ngd-info-label">
            <v-icon x-small class="mr-1">date_range</v-icon>Window
          </span>
          <span class="ngd-info-value">Friday → Monday</span>
          <span class="ngd-info-sub ngd-info-sub--window">4 days open</span>
        </div>
        <div v-if="collecting_cycle === 'Monthly'" class="ngd-info-item ngd-info-item--window">
          <div class="ngd-info-accent"></div>
          <span class="ngd-info-label">
            <v-icon x-small class="mr-1">calendar_today</v-icon>Window
          </span>
          <span class="ngd-info-value">20th → 10th</span>
        </div>
        <div v-if="collecting_cycle === 'Custom' && customSettings" class="ngd-info-item ngd-info-item--custom">
          <div class="ngd-info-accent"></div>
          <span class="ngd-info-label">
            <v-icon x-small class="mr-1">tune</v-icon>Allowed Days
          </span>
          <span class="ngd-info-value">{{ customSettings.allowed_days_display }}</span>
        </div>
        <div v-if="!collection_activated" class="ngd-info-item ngd-info-item--start">
          <div class="ngd-info-accent"></div>
          <span class="ngd-info-label">
            <v-icon x-small class="mr-1">event_available</v-icon>Start Date
          </span>
          <span class="ngd-info-value">{{ start_date || '—' }}</span>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════
         PENALTY ALERT CARD
    ══════════════════════════════════════════════ -->
    <transition name="fade-slide">
      <div v-if="!groupPending && userPenalties.has_penalties" class="ngd-penalty-card">
        <div class="ngd-penalty-header">
          <v-icon color="white" class="mr-2">block</v-icon>
          <span>Contributions Blocked — Unpaid Penalties</span>
        </div>
        <div class="ngd-penalty-body">
          <div class="ngd-penalty-amounts">
            <div class="ngd-penalty-amt-item">
              <div class="ngd-penalty-amt-label">Missed</div>
              <div class="ngd-penalty-amt-val missed">{{ formatAmount(userPenalties.total_contribution) }}</div>
              <div class="ngd-penalty-amt-cur">FCFA</div>
            </div>
            <div class="ngd-penalty-divider">+</div>
            <div class="ngd-penalty-amt-item">
              <div class="ngd-penalty-amt-label">Penalties</div>
              <div class="ngd-penalty-amt-val penalty">{{ formatAmount(userPenalties.total_penalty) }}</div>
              <div class="ngd-penalty-amt-cur">FCFA</div>
            </div>
            <div class="ngd-penalty-divider">=</div>
            <div class="ngd-penalty-amt-item">
              <div class="ngd-penalty-amt-label">Total Due</div>
              <div class="ngd-penalty-amt-val total">{{ formatAmount(userPenalties.total_to_pay) }}</div>
              <div class="ngd-penalty-amt-cur">FCFA</div>
            </div>
          </div>
          <p class="ngd-penalty-note">
            <v-icon small class="mr-1">info</v-icon>
            {{ userPenalties.penalties.length }} period(s) affected. Pay to restore contribution rights and collection eligibility.
          </p>
        </div>
      </div>
    </transition>

    <!-- ═══════════════════════════════════════════
         ACTION ROW
    ══════════════════════════════════════════════ -->
    <div v-if="!groupPending" class="ngd-action-row">

      <button
        v-if="userPenalties.has_penalties"
        class="ngd-btn ngd-btn-penalty"
        @click="showPenaltyDialog = true"
      >
        <v-icon small class="mr-2">warning</v-icon>
        Pay {{ formatAmount(userPenalties.total_to_pay) }} FCFA
      </button>

      <v-tooltip bottom v-if="userPenalties.has_penalties" content-class="ngd-tooltip">
        <template v-slot:activator="{ on }">
          <div v-on="on" class="ngd-btn ngd-btn-blocked ngd-btn-disabled">
            <v-icon small class="mr-2">lock</v-icon>Contribution Blocked
          </div>
        </template>
        <span>Pay your penalties above to unlock contributions.</span>
      </v-tooltip>

      <router-link
        v-else-if="collecting_cycle === 'Custom'"
        :to="'/njangi/groups/contribute/'+njangi_id"
        class="ngd-btn ngd-btn-contribute"
      >
        <v-icon small class="mr-2">add_circle</v-icon>Contribute Now
      </router-link>

      <router-link
        v-else-if="collecting_cycle === 'Daily'"
        :to="'/njangi/groups/contribute/'+njangi_id"
        class="ngd-btn ngd-btn-contribute"
      >
        <v-icon small class="mr-2">add_circle</v-icon>Contribute Now
      </router-link>

      <v-tooltip bottom v-else-if="collecting_cycle === 'Weekly' && !isInWeeklyWindow" content-class="ngd-tooltip">
        <template v-slot:activator="{ on }">
          <div v-on="on" class="ngd-btn ngd-btn-window-closed ngd-btn-disabled">
            <v-icon small class="mr-2">event_busy</v-icon>Window Closed
          </div>
        </template>
        <span>Opens Friday. Next: <strong>{{ nextContributionWindow }}</strong></span>
      </v-tooltip>

      <router-link
        v-else-if="collecting_cycle === 'Weekly' && isInWeeklyWindow"
        :to="'/njangi/groups/contribute/'+njangi_id"
        class="ngd-btn ngd-btn-contribute ngd-btn-pulse"
      >
        <v-icon small class="mr-2">add_circle</v-icon>Contribute Now
      </router-link>

      <v-tooltip bottom v-else-if="collecting_cycle === 'Monthly' && !isInMonthlyWindow" content-class="ngd-tooltip">
        <template v-slot:activator="{ on }">
          <div v-on="on" class="ngd-btn ngd-btn-window-closed ngd-btn-disabled">
            <v-icon small class="mr-2">event_busy</v-icon>Window Closed
          </div>
        </template>
        <span>Opens the 20th. Missed amounts become penalties on the 11th–19th.</span>
      </v-tooltip>

      <router-link
        v-else-if="collecting_cycle === 'Monthly' && isInMonthlyWindow"
        :to="'/njangi/groups/contribute/'+njangi_id"
        class="ngd-btn ngd-btn-contribute"
      >
        <v-icon small class="mr-2">add_circle</v-icon>Contribute Now
      </router-link>

      <div v-else class="ngd-btn ngd-btn-disabled">
        <v-icon small class="mr-2">block</v-icon>Unavailable
      </div>
    </div>

    <!-- ═══════════════════════════════════════════
         CURRENT PERIOD STATS CARD
    ══════════════════════════════════════════════ -->
    <div v-if="!groupPending" class="ngd-stats-card">
      <div class="ngd-stats-header">
        <div>
          <div class="ngd-stats-period-label">
            <v-icon small class="mr-1">today</v-icon>
            <span v-if="collecting_cycle === 'Weekly'">This Week — {{ formatCurrentWeekRange() }}</span>
            <span v-else-if="collecting_cycle === 'Daily'">Today</span>
            <span v-else-if="collecting_cycle === 'Monthly'">This Month</span>
            <span v-else-if="collecting_cycle === 'Custom'">{{ currentPeriodTitle }}</span>
            <span v-else>Current Period</span>
          </div>
          <div v-if="collecting_cycle === 'Weekly'" class="ngd-stats-period-range">
            {{ getCurrentWeekRange() }}
          </div>
          <div v-if="collecting_cycle === 'Custom' && customPeriodInfo" class="ngd-stats-period-range">
            {{ customPeriodInfo.display || currentPeriodSubtitle }}
          </div>
        </div>
        <div v-if="current_collector" class="ngd-collector-badge">
          <v-icon x-small class="mr-1" color="white">emoji_events</v-icon>
          {{ current_collector }}
          <span v-if="current_collect_position" class="ngd-collector-pos">#{{ current_collect_position }}</span>
        </div>
      </div>

      <div class="ngd-stats-body">
        <div class="ngd-progress-section">
          <div class="ngd-ring-wrap">
            <svg viewBox="0 0 80 80" class="ngd-ring">
              <circle cx="40" cy="40" r="32" class="ngd-ring-bg" />
              <circle
                cx="40" cy="40" r="32"
                class="ngd-ring-fill"
                :style="ringStyle"
              />
            </svg>
            <div class="ngd-ring-label">{{ displayContributionPercentage }}%</div>
          </div>
          <div class="ngd-progress-meta">
            <div class="ngd-progress-row">
              <div class="ngd-prog-dot ngd-dot-success"></div>
              <span>{{ displayContributedCount }} contributed</span>
            </div>
            <div class="ngd-progress-row">
              <div class="ngd-prog-dot ngd-dot-error"></div>
              <span>{{ displayNotContributedCount }} pending</span>
            </div>
            <div class="ngd-progress-row ngd-progress-total">
              <span>{{ formatAmount(displayContributedAmount) }}</span>
              <span class="ngd-slash"> / </span>
              <span>{{ formatAmount(displayExpectedAmount) }} FCFA</span>
            </div>
          </div>
        </div>

        <div class="ngd-bar-wrap">
          <div class="ngd-bar-track">
            <div
              class="ngd-bar-fill"
              :class="displayContributionPercentage === 100 ? 'ngd-bar-complete' : ''"
              :style="{ width: displayContributionPercentage + '%' }"
            ></div>
          </div>
          <span class="ngd-bar-pct">{{ displayContributionPercentage }}%</span>
        </div>

        <div v-if="collection_activated && collection_date" class="ngd-collection-date">
          <v-icon x-small class="mr-1">event_available</v-icon>
          Collection date: <strong>{{ collection_date }}</strong>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════
         REQUESTED TRANSACTIONS
    ══════════════════════════════════════════════ -->
    <div v-if="!groupPending && njangiRequestedTransactions.length > 0" class="ngd-transactions">
      <div class="ngd-section-title">
        <v-icon small class="mr-2">receipt_long</v-icon>Pending Transactions
      </div>
      <div class="ngd-txn-list">
        <div
          v-for="item in njangiRequestedTransactions"
          :key="item.id"
          v-if="!item.cancelled || item.withdrawal || (!item.withdrawal && !item.confirmed)"
          class="ngd-txn-row"
        >
          <div class="ngd-txn-left">
            <div class="ngd-txn-icon" :class="item.withdrawal ? 'ngd-txn-out' : 'ngd-txn-in'">
              <v-icon small color="white">{{ item.withdrawal ? 'arrow_upward' : 'arrow_downward' }}</v-icon>
            </div>
            <div>
              <div class="ngd-txn-type">{{ item.withdrawal ? 'Withdrawal' : 'Deposit' }}</div>
              <div class="ngd-txn-amount">{{ formatAmount(item.amount) }} FCFA</div>
            </div>
          </div>
          <div class="ngd-txn-right">
            <div class="ngd-txn-status" :class="item.confirmed ? 'ngd-txn-confirmed' : 'ngd-txn-pending'">
              {{ item.confirmed ? 'Confirmed' : 'Pending' }}
            </div>
            <div class="ngd-txn-date">{{ item.get_date_str }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════
         CONTRIBUTION HISTORY
    ══════════════════════════════════════════════ -->
    <div class="ngd-history-card">
      <div class="ngd-section-title">
        <v-icon small class="mr-2">history</v-icon>Member Contributions
      </div>

      <div v-if="groupPending" class="ngd-history-pending">
        <v-icon size="56" color="#b0bec5">hourglass_empty</v-icon>
        <p class="ngd-history-pending-text">{{ pendingMessage || 'History available after group approval.' }}</p>
      </div>

      <template v-else>
        <!-- ── Period Selector ── -->
        <div class="ngd-period-selector">
          <div class="ngd-period-label">
            <v-icon small class="mr-1">event</v-icon>Select Period
          </div>

          <div class="ngd-period-controls">
            <!-- Daily -->
            <v-menu v-if="collecting_cycle === 'Daily'" v-model="dateMenuDaily"
              :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <div class="ngd-date-input-wrap" v-bind="attrs" v-on="on">
                  <v-icon small class="ngd-date-icon">event</v-icon>
                  <input readonly class="ngd-date-input" :value="date_history_display" placeholder="Select date" />
                  <v-icon small class="ngd-date-caret">expand_more</v-icon>
                </div>
              </template>
              <v-date-picker v-model="date_history" @input="dateMenuDaily = false; handleDateChange()" no-title scrollable></v-date-picker>
            </v-menu>

            <!-- Weekly -->
            <div v-else-if="collecting_cycle === 'Weekly'" class="ngd-select-row">
              <div class="ngd-select-wrap">
                <v-icon small class="ngd-date-icon">date_range</v-icon>
                <select class="ngd-select" v-model="selectedWeek" @change="updateWeeklyDate">
                  <option v-for="w in weekOptions" :key="w.value" :value="w.value">{{ w.text }}</option>
                </select>
              </div>
              <div class="ngd-select-wrap">
                <select class="ngd-select" v-model="selectedYear" @change="updateWeeklyDate">
                  <option v-for="y in yearOptions" :key="y.value" :value="y.value">{{ y.text }}</option>
                </select>
              </div>
            </div>

            <!-- Monthly -->
            <div v-else-if="collecting_cycle === 'Monthly'" class="ngd-select-row">
              <div class="ngd-select-wrap">
                <v-icon small class="ngd-date-icon">calendar_today</v-icon>
                <select class="ngd-select" v-model="selectedMonth" @change="updateMonthlyDate">
                  <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.text }}</option>
                </select>
              </div>
              <div class="ngd-select-wrap">
                <select class="ngd-select" v-model="selectedYear" @change="updateMonthlyDate">
                  <option v-for="y in yearOptions" :key="y.value" :value="y.value">{{ y.text }}</option>
                </select>
              </div>
            </div>

            <!-- Custom -->
            <v-menu v-else-if="collecting_cycle === 'Custom'" v-model="dateMenuCustom"
              :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <div class="ngd-date-input-wrap" v-bind="attrs" v-on="on">
                  <v-icon small class="ngd-date-icon">tune</v-icon>
                  <input readonly class="ngd-date-input" :value="date_history_display" placeholder="Select date" />
                  <v-icon small class="ngd-date-caret">expand_more</v-icon>
                </div>
              </template>
              <v-date-picker v-model="date_history" @input="dateMenuCustom = false; handleDateChange()"
                no-title scrollable :min="customDateMin" :max="customDateMax">
              </v-date-picker>
            </v-menu>

            <button class="ngd-refresh-btn" @click="refreshHistory()" :disabled="isRefreshing">
              <v-icon small :class="{ 'ngd-spin': isRefreshing }">refresh</v-icon>
              <span class="ngd-refresh-label">Refresh</span>
            </button>
          </div>

          <div v-if="dateContextInfo" class="ngd-date-ctx">
            <v-icon x-small class="mr-1">{{ dateContextIcon }}</v-icon>{{ dateContextInfo }}
          </div>
        </div>

        <!-- ── History progress bar ── -->
        <div class="ngd-hist-bar-wrap">
          <div class="ngd-hist-bar-track">
            <div class="ngd-hist-bar-fill" :style="{ width: historyPercentage + '%' }"
              :class="historyPercentage === 100 ? 'ngd-hist-bar-full' : ''"></div>
          </div>
          <div class="ngd-hist-bar-meta">
            <span>{{ formatAmount(history_contributed_amount) }} / {{ formatAmount(history_expected_amount) }} FCFA</span>
            <span class="ngd-hist-pct">{{ historyPercentage }}%</span>
          </div>
        </div>

        <div class="ngd-last-update">
          <v-icon x-small class="mr-1">access_time</v-icon>Last updated: {{ lastUpdateTime }}
        </div>

        <!-- ── Member list ── -->
        <transition-group name="member-list" tag="div" class="ngd-member-list">
          <div
            v-for="item in njangi_members"
            :key="item._key"
            class="ngd-member-row"
            :class="item.contributed ? 'ngd-member-ok' : 'ngd-member-nok'"
          >
            <div class="ngd-member-avatar" :class="item.contributed ? 'ngd-avatar-ok' : 'ngd-avatar-nok'">
              <v-icon small color="white">{{ item.contributed ? 'check' : 'close' }}</v-icon>
            </div>
            <div class="ngd-member-info">
              <div class="ngd-member-name">
                {{ item.member_name }}
                <span v-if="item.position && item.position !== 9999" class="ngd-position-tag">
                  #{{ item.position }}
                </span>
              </div>
              <div class="ngd-member-tags">
                <span class="ngd-tag" :class="item.contributed ? 'ngd-tag-ok' : 'ngd-tag-nok'">
                  {{ item.contributed ? '✅ Contributed' : '❌ Not Contributed' }}
                </span>
                <span v-if="item.has_penalty" class="ngd-tag ngd-tag-penalty">
                  <v-icon x-small class="mr-1">warning</v-icon>Penalty: {{ formatAmount(item.penalty_amount) }} FCFA
                </span>
              </div>
            </div>
          </div>
        </transition-group>

        <!-- Empty state -->
        <div v-if="!njangi_members || njangi_members.length === 0" class="ngd-empty">
          <v-icon size="52" color="#cfd8dc">group</v-icon>
          <p>No data for this period.</p>
        </div>
      </template>
    </div>

    <!-- ═══════════════════════════════════════════
         PENALTY PAYMENT DIALOG
    ══════════════════════════════════════════════ -->
    <v-dialog v-model="showPenaltyDialog" max-width="680px" persistent scrollable>
      <div class="ngd-dialog">
        <div class="ngd-dialog-header">
          <v-icon color="white" class="mr-2" size="22">warning</v-icon>
          Pay Penalties &amp; Missed Contributions
          <button class="ngd-dialog-close" @click="closePenaltyDialog()">
            <v-icon color="white">close</v-icon>
          </button>
        </div>

        <div class="ngd-dialog-body">
          <div class="ngd-dialog-totals">
            <div class="ngd-dialog-total-item">
              <div class="ngd-dialog-total-label">Missed</div>
              <div class="ngd-dialog-total-val missed">{{ formatAmount(userPenalties.total_contribution) }}</div>
              <div class="ngd-dialog-total-cur">FCFA</div>
            </div>
            <div class="ngd-plus">+</div>
            <div class="ngd-dialog-total-item">
              <div class="ngd-dialog-total-label">Penalties</div>
              <div class="ngd-dialog-total-val penalty">{{ formatAmount(userPenalties.total_penalty) }}</div>
              <div class="ngd-dialog-total-cur">FCFA</div>
            </div>
            <div class="ngd-plus">=</div>
            <div class="ngd-dialog-total-item ngd-total-highlight">
              <div class="ngd-dialog-total-label">Total Due</div>
              <div class="ngd-dialog-total-val total">{{ formatAmount(userPenalties.total_to_pay) }}</div>
              <div class="ngd-dialog-total-cur">FCFA</div>
            </div>
          </div>

          <div class="ngd-dialog-section-label">
            <v-icon small class="mr-1">list_alt</v-icon>Breakdown
          </div>
          <div class="ngd-breakdown-list">
            <div
              v-for="(penalty, index) in userPenalties.penalties"
              :key="penalty.id"
              class="ngd-breakdown-row"
            >
              <div class="ngd-breakdown-num">{{ index + 1 }}</div>
              <div class="ngd-breakdown-body">
                <div class="ngd-breakdown-period">
                  <v-icon x-small class="mr-1" :color="getPenaltyTypeColor(penalty)">{{ getPenaltyTypeIcon(penalty) }}</v-icon>
                  {{ formatPenaltyPeriod(penalty) }}
                </div>
                <div class="ngd-breakdown-chips">
                  <span class="ngd-bchip ngd-bchip-missed">
                    Missed: {{ formatAmount(penalty.contribution_amount) }}
                  </span>
                  <span class="ngd-bchip ngd-bchip-penalty">
                    Penalty: {{ formatAmount(penalty.penalty_amount) }}
                  </span>
                  <span class="ngd-bchip ngd-bchip-total">
                    Total: {{ formatAmount(penalty.total_to_pay) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="ngd-dialog-section-label">
            <v-icon small class="mr-1">phone</v-icon>Payment Details
          </div>

          <div class="ngd-phone-input-wrap">
            <v-icon small class="ngd-phone-icon">phone</v-icon>
            <input
              v-model="penaltyPhone"
              class="ngd-phone-input"
              type="tel"
              placeholder="Input your phone number"
            />
          </div>

          <div class="ngd-dialog-section-label">
            <v-icon small class="mr-1">credit_card</v-icon>Operator
          </div>

          <div class="ngd-operator-row">
            <div
              class="ngd-operator-card"
              :class="{ 'ngd-operator-selected-momo': penaltyMomoChoice }"
              @click="selectPenaltyMoMo()"
            >
              <v-img
                :src="require('@/assets/mtn-mobile-money-logo.png')"
                contain
                height="48"
                class="mb-2"
              ></v-img>
              <div class="ngd-operator-name">MTN MoMo</div>
              <v-icon v-if="penaltyMomoChoice" small color="#FFC107">check_circle</v-icon>
            </div>
            <div
              class="ngd-operator-card"
              :class="{ 'ngd-operator-selected-om': penaltyOmChoice }"
              @click="selectPenaltyOM()"
            >
              <v-img
                :src="require('@/assets/om.png')"
                contain
                height="48"
                class="mb-2"
              ></v-img>
              <div class="ngd-operator-name">Orange Money</div>
              <v-icon v-if="penaltyOmChoice" small color="#FF9800">check_circle</v-icon>
            </div>
          </div>

          <div class="ngd-info-note">
            <v-icon x-small class="mr-1">info</v-icon>
            Payments verified within 24 hours.
          </div>
        </div>

        <div class="ngd-dialog-footer">
          <button class="ngd-btn ngd-btn-cancel" @click="closePenaltyDialog()">
            Cancel
          </button>
          <button
            class="ngd-btn ngd-btn-pay"
            :disabled="!penaltyPhone || !penaltyOperator || isSubmittingPenalty"
            @click="submitPenaltyPayment()"
          >
            <v-icon small class="mr-2">payment</v-icon>
            Pay {{ formatAmount(userPenalties.total_to_pay) }} FCFA
            <v-progress-circular v-if="isSubmittingPenalty" indeterminate size="16" width="2" color="white" class="ml-2"></v-progress-circular>
          </button>
        </div>
      </div>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios'
import 'animate.css'

export default {
  data() {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1
    const currentWeek = this.getNjangiWeekNumber(currentDate)

    return {
      groupPending: false,
      pendingMessage: '',
      njangi_id: '',
      njangi_members: [],
      njangi_group_name: '',
      njangi_contact: '',
      njangi_group_description: '',
      contribution_amount: '',
      collecting_cycle: '',
      no_contributed: null,
      no_members: null,
      njangiRequestedTransactions: [],
      date_history: '',
      collection_date: '',
      start_date: '',
      collection_activated: '',
      contributed_amount: '',
      expected_amount: '',
      current_collector: '',
      current_collect_position: null,
      history_contributed_amount: '',
      history_expected_amount: '',
      userPenalties: {
        penalties: [],
        total_penalty: 0,
        total_contribution: 0,
        total_to_pay: 0,
        has_penalties: false
      },
      showPenaltyDialog: false,
      penaltyPhone: '',
      penaltyMomoChoice: false,
      penaltyOmChoice: false,
      penaltyOperator: '',
      penaltyPaymentRef: '',
      isSubmittingPenalty: false,
      showPenaltyTooltip: false,
      showWindowTooltip: false,
      isInWeeklyWindow: true,
      nextContributionWindow: '',
      penaltyCheckInterval: null,
      isRefreshing: false,
      lastUpdateTime: '',

      dateMenuDaily: false,
      dateMenuCustom: false,
      date_history_display: currentDate.toISOString().split('T')[0],
      selectedWeek: currentWeek.toString().padStart(2, '0'),
      selectedMonth: currentMonth.toString().padStart(2, '0'),
      selectedYear: currentYear.toString(),

      weekOptions: Array.from({ length: 52 }, (_, i) => {
        const weekNum = (i + 1).toString().padStart(2, '0')
        return { text: `Week ${weekNum}`, value: weekNum }
      }),
      monthOptions: [
        { text: 'January', value: '01' }, { text: 'February', value: '02' },
        { text: 'March', value: '03' }, { text: 'April', value: '04' },
        { text: 'May', value: '05' }, { text: 'June', value: '06' },
        { text: 'July', value: '07' }, { text: 'August', value: '08' },
        { text: 'September', value: '09' }, { text: 'October', value: '10' },
        { text: 'November', value: '11' }, { text: 'December', value: '12' }
      ],
      yearOptions: Array.from({ length: 5 }, (_, i) => {
        const year = currentYear - 2 + i
        return { text: year.toString(), value: year.toString() }
      }),

      customSettings: null,
      customPeriodInfo: null,
      customDateMin: null,
      customDateMax: null
    }
  },

  computed: {
    cycleBadgeClass() {
      const map = {
        Weekly: 'ngd-cycle-weekly',
        Daily: 'ngd-cycle-daily',
        Monthly: 'ngd-cycle-monthly',
        Custom: 'ngd-cycle-custom'
      }
      return map[this.collecting_cycle] || ''
    },

    cycleIcon() {
      const map = {
        Weekly: 'date_range',
        Daily: 'today',
        Monthly: 'calendar_today',
        Custom: 'tune'
      }
      return map[this.collecting_cycle] || 'schedule'
    },

    ringStyle() {
      const pct = this.displayContributionPercentage
      const circumference = 2 * Math.PI * 32
      const offset = circumference - (pct / 100) * circumference
      return {
        strokeDasharray: circumference,
        strokeDashoffset: offset,
        stroke: pct === 100 ? '#43a047' : '#1565c0'
      }
    },

    displayContributedCount() {
      if (!this.njangi_members || !this.njangi_members.length) return 0
      return this.njangi_members.filter(m => m.contributed).length
    },
    displayTotalMembers() {
      if (this.njangi_members && this.njangi_members.length > 0) return this.njangi_members.length
      return this.no_members || 0
    },
    displayNotContributedCount() {
      return this.displayTotalMembers - this.displayContributedCount
    },
    displayContributedAmount() { return this.history_contributed_amount || 0 },
    displayExpectedAmount() { return this.history_expected_amount || this.expected_amount || 0 },
    displayContributionPercentage() {
      if (!this.displayExpectedAmount || this.displayExpectedAmount === 0) return 0
      return Math.round((this.displayContributedAmount / this.displayExpectedAmount) * 100)
    },

    canContribute() {
      if (this.groupPending) return false
      if (this.userPenalties.has_penalties) return false
      if (this.collecting_cycle === 'Weekly') return this.isInWeeklyWindow
      if (this.collecting_cycle === 'Monthly') return this.isInMonthlyWindow
      return true
    },

    isInMonthlyWindow() {
      const day = new Date().getDate()
      return day >= 20 || day <= 10
    },

    historyPercentage() {
      if (!this.history_expected_amount || this.history_expected_amount === 0) return 0
      return Math.round((this.history_contributed_amount / this.history_expected_amount) * 100)
    },

    apiDateFormat() {
      if (!this.date_history) return this.getDefaultDate()
      switch (this.collecting_cycle) {
        case 'Daily': case 'Custom': return this.date_history
        case 'Weekly':
          return this.date_history.includes('-W')
            ? this.date_history
            : `${this.selectedYear}-W${this.selectedWeek}`
        case 'Monthly':
          return this.date_history.match(/^\d{4}-\d{2}$/)
            ? this.date_history
            : `${this.selectedYear}-${this.selectedMonth}`
        default: return this.getDefaultDate()
      }
    },

    currentPeriodTitle() {
      if (this.collecting_cycle === 'Custom' && this.customPeriodInfo) return this.customPeriodInfo.title || 'Current Period'
      return 'Current Period'
    },
    currentPeriodSubtitle() {
      if (this.collecting_cycle === 'Custom' && this.customPeriodInfo) return this.customPeriodInfo.subtitle || ''
      return ''
    },

    dateContextInfo() {
      if (!this.date_history) return ''
      try {
        switch (this.collecting_cycle) {
          case 'Daily': {
            const d = new Date(this.date_history)
            return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
          }
          case 'Weekly': {
            const wi = this.getNjangiWeekInfo(this.date_history)
            if (wi && wi.tuesdayStart) {
              const t = wi.tuesdayStart.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
              const m = wi.mondayEnd.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
              return `${t} → ${m}`
            }
            return ''
          }
          case 'Monthly': {
            const [year, month] = this.date_history.split('-')
            return new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
          }
          case 'Custom':
            if (this.customPeriodInfo) return this.customPeriodInfo.display || this.customPeriodInfo.subtitle
            return new Date(this.date_history).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
          default: return ''
        }
      } catch { return '' }
    },

    dateContextIcon() {
      const map = { Daily: 'event', Weekly: 'date_range', Monthly: 'calendar_today', Custom: 'tune' }
      return map[this.collecting_cycle] || 'event'
    }
  },

  watch: {
    date_history(newVal) {
      if (!newVal) return
      if (this.collecting_cycle === 'Daily' || this.collecting_cycle === 'Custom') {
        this.date_history_display = newVal
      } else if (this.collecting_cycle === 'Weekly' && newVal.includes('-W')) {
        const match = newVal.match(/^(\d{4})-W(\d{2})$/)
        if (match) { this.selectedYear = match[1]; this.selectedWeek = match[2] }
      } else if (this.collecting_cycle === 'Monthly' && newVal.match(/^\d{4}-\d{2}$/)) {
        const [year, month] = newVal.split('-')
        this.selectedYear = year; this.selectedMonth = month
      }
    },
    collecting_cycle(newCycle) {
      if (newCycle === 'Custom') this.loadCustomSettings()
    }
  },

  async mounted() {
    document.title = 'PettyCash | Njangi Group'
    await this.refreshGroupDetailData()
    this.penaltyCheckInterval = setInterval(() => { this.getUserPenalties() }, 60000)
  },

  beforeRouteUpdate(to, from, next) {
    if (to.params.key !== from.params.key) this.refreshGroupDetailData()
    next()
  },

  beforeDestroy() {
    if (this.penaltyCheckInterval) clearInterval(this.penaltyCheckInterval)
  },

  methods: {
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0'
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    },
    formatPhone(phone) {
      if (!phone) return ''
      const cleaned = phone.toString().replace(/\D/g, '')
      if (cleaned.startsWith('237') && cleaned.length === 12) {
        return `+237 ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`
      }
      if (cleaned.length === 9) {
        return `+237 ${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
      }
      return phone
    },

    getPenaltyTypeIcon(penalty) {
      if (penalty.period_identifier.match(/Week \d+/)) return 'date_range'
      if (penalty.period_identifier.match(/^\d{4}-\d{2}-\d{2}$/)) return 'event'
      return 'calendar_today'
    },

    getPenaltyTypeColor(penalty) {
      if (penalty.period_identifier.match(/Week \d+/)) return 'purple darken-1'
      if (penalty.period_identifier.match(/^\d{4}-\d{2}-\d{2}$/)) return 'blue darken-1'
      return 'teal darken-1'
    },

    formatPenaltyPeriod(penalty) {
      try {
        const sessionDate = new Date(penalty.session_date_raw)
        if (penalty.period_identifier.match(/Week \d+/)) {
          const tuesday = sessionDate
          const monday = new Date(tuesday); monday.setDate(monday.getDate() + 6)
          const match = penalty.period_identifier.match(/Week (\d+) of (\d+)/)
          if (match) return `${match[2]}-W${match[1].padStart(2, '0')} (${this.formatShortDate(tuesday)} → ${this.formatFullDate(monday)})`
          return `${this.formatShortDate(tuesday)} → ${this.formatFullDate(monday)}`
        } else if (penalty.period_identifier.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return this.formatFullDate(sessionDate) + ` (${this.getDayName(sessionDate)})`
        } else if (this.collecting_cycle === 'Monthly') {
          const startDay = new Date(sessionDate.getFullYear(), sessionDate.getMonth() - 1, 20)
          const endDay = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), 10)
          const monthName = sessionDate.toLocaleDateString('en-US', { month: 'long' })
          return `${monthName} ${sessionDate.getFullYear()} (${this.formatShortDate(startDay)} → ${this.formatFullDate(endDay)})`
        } else {
          const firstDay = sessionDate
          const lastDay = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0)
          const monthName = firstDay.toLocaleDateString('en-US', { month: 'long' })
          return `${monthName} ${firstDay.getFullYear()} (${this.formatShortDate(firstDay)} → ${this.formatShortDate(lastDay)})`
        }
      } catch { return penalty.period_identifier }
    },

    formatShortDate(date) { return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) },
    formatFullDate(date) { return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) },
    getDayName(date) { return date.toLocaleDateString('en-US', { weekday: 'long' }) },

    async loadCustomSettings() {
      const group_id = this.$route.params.key
      try {
        const response = await axios.get(`/api/v1/njangi/get/custom/group/settings/${group_id}/`)
        if (response.data && !response.data.error) {
          this.customSettings = response.data
          if (this.customSettings.start_date) {
            this.customDateMin = new Date(this.customSettings.start_date).toISOString().split('T')[0]
            const max = new Date(); max.setFullYear(max.getFullYear() + 1)
            this.customDateMax = max.toISOString().split('T')[0]
          }
        }
      } catch {
        this.customSettings = { interval_value: 7, interval_unit: 'days', allowed_days_display: 'Monday, Friday, Saturday, Sunday', penalty_amount: 500 }
      }
    },

    getNjangiWeekNumber(date) {
      const d = new Date(date); d.setHours(0, 0, 0, 0)
      const dayOfWeek = d.getDay()
      if (dayOfWeek === 1) d.setDate(d.getDate() - 1)
      d.setDate(d.getDate() + 4 - (d.getDay() || 7))
      const yearStart = new Date(d.getFullYear(), 0, 1)
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
    },

    getNjangiWeekInfo(isoWeekString) {
      try {
        const [year, weekStr] = isoWeekString.split('-W')
        if (!year || !weekStr) throw new Error('Invalid week format')
        const week = parseInt(weekStr, 10), yearNum = parseInt(year, 10)
        const jan4 = new Date(yearNum, 0, 4), jan4Day = jan4.getDay() || 7
        const mondayISOWeek1 = new Date(jan4); mondayISOWeek1.setDate(jan4.getDate() - (jan4Day - 1))
        const targetMonday = new Date(mondayISOWeek1); targetMonday.setDate(mondayISOWeek1.getDate() + (week - 1) * 7)
        const tuesdayStart = new Date(targetMonday); tuesdayStart.setDate(targetMonday.getDate() + 1)
        const mondayEnd = new Date(tuesdayStart); mondayEnd.setDate(tuesdayStart.getDate() + 6)
        return { year: yearNum, week, tuesdayStart, mondayEnd, isoString: isoWeekString }
      } catch {
        const now = new Date(), year = now.getFullYear(), week = this.getNjangiWeekNumber(now)
        const jan4 = new Date(year, 0, 4), jan4Day = jan4.getDay() || 7
        const mondayISOWeek1 = new Date(jan4); mondayISOWeek1.setDate(jan4.getDate() - (jan4Day - 1))
        const targetMonday = new Date(mondayISOWeek1); targetMonday.setDate(mondayISOWeek1.getDate() + (week - 1) * 7)
        const tuesdayStart = new Date(targetMonday); tuesdayStart.setDate(targetMonday.getDate() + 1)
        const mondayEnd = new Date(tuesdayStart); mondayEnd.setDate(tuesdayStart.getDate() + 6)
        return { year, week, tuesdayStart, mondayEnd, isoString: `${year}-W${week.toString().padStart(2, '0')}` }
      }
    },

    getCurrentWeekRange() {
      if (this.collecting_cycle !== 'Weekly') return ''
      try {
        const wi = this.getNjangiWeekInfo(this.date_history)
        if (wi && wi.tuesdayStart && wi.mondayEnd) {
          return `${wi.tuesdayStart.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} → ${wi.mondayEnd.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`
        }
        return ''
      } catch { return '' }
    },

    formatCurrentWeekRange() {
      if (this.collecting_cycle !== 'Weekly') return ''
      try {
        const wi = this.getNjangiWeekInfo(this.date_history)
        return wi ? `Week ${wi.week}, ${wi.year}` : ''
      } catch { return '' }
    },

    initializeDateSelectors() {
      const now = new Date()
      this.selectedYear = now.getFullYear().toString()
      this.selectedMonth = (now.getMonth() + 1).toString().padStart(2, '0')
      this.selectedWeek = this.getNjangiWeekNumber(now).toString().padStart(2, '0')
      this.date_history_display = now.toISOString().split('T')[0]
      const yr = now.getFullYear()
      this.yearOptions = Array.from({ length: 5 }, (_, i) => { const y = yr - 2 + i; return { text: y.toString(), value: y.toString() } })
      this.setDefaultDate()
    },

    setDefaultDate() {
      const now = new Date()
      switch (this.collecting_cycle) {
        case 'Daily':
          this.date_history = now.toISOString().split('T')[0]
          break
        case 'Weekly': {
          const w = this.getNjangiWeekNumber(now)
          this.date_history = `${now.getFullYear()}-W${w.toString().padStart(2, '0')}`
          break
        }
        case 'Monthly': {
          const d = new Date()
          const targetMonth = d.getDate() >= 20 ? (d.getMonth() + 2 > 12 ? 1 : d.getMonth() + 2) : (d.getMonth() + 1)
          const targetYear = d.getDate() >= 20 && d.getMonth() + 2 > 12 ? d.getFullYear() + 1 : d.getFullYear()
          this.date_history = `${targetYear}-${targetMonth.toString().padStart(2, '0')}`
          break
        }
        case 'Custom':
          this.date_history = now.toISOString().split('T')[0]
          break
        default:
          this.date_history = now.toISOString().split('T')[0]
      }
      this.date_history_display = this.date_history
    },

    updateWeeklyDate() {
      if (this.selectedYear && this.selectedWeek) {
        this.date_history = `${this.selectedYear}-W${this.selectedWeek}`
        this.$nextTick(() => { this.handleDateChange() })
      }
    },

    updateMonthlyDate() {
      if (this.selectedYear && this.selectedMonth) {
        this.date_history = `${this.selectedYear}-${this.selectedMonth}`
        this.$nextTick(() => { this.handleDateChange() })
      }
    },

    async handleDateChange() { await this.getContribHistory() },

    async refreshHistory(silent = false) {
      this.isRefreshing = true
      try {
        await this.getContribHistory()
        if (!silent) {
          this.$store.commit('setSnackBarMessage', '✅ History refreshed')
          this.$store.commit('setSnackBarColor', 'success')
          this.$store.commit('activateSnackBar', true)
          this.$store.commit('setSnackBarTimeOut', 2000)
        }
      } catch {
        if (!silent) {
          this.$store.commit('setSnackBarMessage', '❌ Failed to refresh')
          this.$store.commit('setSnackBarColor', 'error')
          this.$store.commit('activateSnackBar', true)
        }
      }
      this.isRefreshing = false
    },

    async refreshGroupDetailData() {
      this.initializeDateSelectors()
      await this.getNjangiGroupSetting()
      this.getClientRequestedNjangiTransactions()
      this.getUserPenalties()
      this.checkWeeklyContributionWindow()
      if (this.collecting_cycle === 'Custom') await this.loadCustomSettings()
      setTimeout(() => this.refreshHistory(true), 500)
    },

    async getUserPenalties() {
      const group_id = this.$route.params.key
      try {
        const response = await axios.get(`/api/v1/njangi/get/user/penalties/${group_id}/`)
        const prev = this.userPenalties.has_penalties
        this.userPenalties = response.data
        if (this.userPenalties.has_penalties && !prev) {
          this.$store.commit('setSnackBarMessage', `⚠️ ${this.userPenalties.penalties.length} unpaid penalty(ies). Total: ${this.userPenalties.total_to_pay} FCFA`)
          this.$store.commit('setSnackBarColor', 'error')
          this.$store.commit('activateSnackBar', true)
          this.$store.commit('setSnackBarTimeOut', 8000)
        }
      } catch { console.error('Error fetching penalties') }
    },

    selectPenaltyMoMo() {
      this.penaltyMomoChoice = true
      this.penaltyOmChoice = false
      this.penaltyOperator = 'MTN Mobile Money'
    },

    selectPenaltyOM() {
      this.penaltyMomoChoice = false
      this.penaltyOmChoice = true
      this.penaltyOperator = 'Orange Money'
    },

    closePenaltyDialog() {
      this.showPenaltyDialog = false
      this.penaltyPhone = ''
      this.penaltyMomoChoice = false
      this.penaltyOmChoice = false
      this.penaltyOperator = ''
    },

    async submitPenaltyPayment() {
      if (!this.penaltyPhone) {
        this.$store.commit('setSnackBarMessage', 'Enter a phone number')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        return
      }
      if (!this.penaltyOperator) {
        this.$store.commit('setSnackBarMessage', 'Select an operator')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        return
      }

      const amount = this.userPenalties.total_to_pay
      let ussdCode = ''
      if (this.penaltyOperator === 'MTN Mobile Money') {
        ussdCode = `*126*4*${this.$store.state.MerchantCodeMTN}*${amount}#`
      } else if (this.penaltyOperator === 'Orange Money') {
        ussdCode = `#150*47*${this.$store.state.MerchantCodeOrange}*${amount}#`
      }

      try {
        const href = `tel:${ussdCode.replace(/#/g, '%23')}`
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        if (isMobile) {
          window.location.href = href
        } else {
          window.open(href, '_blank')
        }
      } catch {
        this.$store.commit('setSnackBarMessage', `Dial: ${ussdCode}`)
        this.$store.commit('setSnackBarColor', 'info')
        this.$store.commit('activateSnackBar', true)
      }

      this.isSubmittingPenalty = true
      this.$store.commit('setIsLoading', true)

      const randomId = Math.floor(Math.random() * 90000000) + 10000000
      this.penaltyPaymentRef = `PENALTY-${this.$route.params.key}-${randomId}`

      const formData = new FormData()
      formData.append('payment_ref', this.penaltyPaymentRef)
      formData.append('phone_number', this.penaltyPhone)
      formData.append('transaction_operator', this.penaltyOperator)
      formData.append('ussd_code', ussdCode)

      try {
        const response = await axios.post(
          `/api/v1/njangi/pay/penalties/${this.$route.params.key}/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        this.$store.commit('setSnackBarMessage', response.data[0].text)
        this.$store.commit('setSnackBarColor', response.data[0].color)
        this.$store.commit('activateSnackBar', true)
        this.$store.commit('setSnackBarTimeOut', 8000)
        if (response.data[0].valid) {
          this.$store.commit('showPaymentVerification', {
            amount: this.userPenalties.total_to_pay,
            operator: this.penaltyOperator,
            phoneNumber: this.penaltyPhone,
            feature: 'njangi',
            id: this.$route.params.key
          })
          this.closePenaltyDialog()
        }
      } catch (error) {
        this.$store.commit('setSnackBarMessage', error.response ? `${error.response.status} ${error.response.statusText}` : 'Network error')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      }

      this.isSubmittingPenalty = false
      this.$store.commit('setIsLoading', false)
    },

    async getClientRequestedNjangiTransactions() {
      const group_id = this.$route.params.key
      try {
        const r = await axios.get(`/api/v1/njangi/get/user/group/${group_id}/requested_transactions/`)
        this.njangiRequestedTransactions = r.data
      } catch (error) {
        this.$store.commit('setSnackBarMessage', error.response ? `${error.response.status} ${error.response.statusText}` : 'Network error')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      }
    },

    checkWeeklyContributionWindow() {
      const dayOfWeek = new Date().getDay()
      if (this.collecting_cycle === 'Weekly') {
        this.isInWeeklyWindow = [5, 6, 0, 1].includes(dayOfWeek)
        if (!this.isInWeeklyWindow) {
          const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7
          const nextFriday = new Date()
          nextFriday.setDate(new Date().getDate() + daysUntilFriday)
          this.nextContributionWindow = nextFriday.toLocaleDateString('en-US', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
          })
        }
      } else {
        this.isInWeeklyWindow = true
        this.nextContributionWindow = 'Always open'
      }
    },

    async getContribHistory() {
      const group_id = this.$route.params.key
      const apiDate = this.apiDateFormat
      try {
        this.njangi_members = []
        await this.$nextTick()
        const response = await axios.get(`/api/v1/njangi/get/user/group/members/${group_id}/${apiDate}/`)
        if (response.data.pending) {
          this.groupPending = true
          this.pendingMessage = response.data.pending_message
          this.njangi_members = []
          this.history_contributed_amount = 0
          this.history_expected_amount = 0
          this.customPeriodInfo = null
        } else {
          this.groupPending = false
          this.customPeriodInfo = response.data.period_info || null
          const ts = Date.now()
          this.njangi_members = response.data.group_members_contrib_info.map((m, i) => ({
            ...m,
            _key: `${m.membership_id}-${ts}-${i}`,
            contributed: Boolean(m.contributed),
            has_penalty: Boolean(m.has_penalty),
            penalty_amount: Number(m.penalty_amount || 0)
          }))
          this.history_contributed_amount = response.data.total_contributed
          this.history_expected_amount = response.data.expected_contribution
          this.lastUpdateTime = new Date().toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit', second: '2-digit'
          })
          await this.$nextTick()
        }
      } catch (error) {
        this.$store.commit('setSnackBarMessage', error.response ? `${error.response.status} ${error.response.statusText}` : 'Network error')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      }
    },

    async getNjangiGroupSetting() {
      this.$store.commit('setIsLoading', true)
      const group_id = this.$route.params.key
      try {
        const response = await axios.get(`/api/v1/njangi/get/user/group/setting/detail/${group_id}/`)
        const s = response.data[1]
        this.njangi_id = s.njangi_group_id
        this.njangi_group_name = s.njangi_group_name
        this.njangi_group_description = s.njangi_group_description
        this.njangi_contact = s.njangi_contact
        this.contribution_amount = s.contribution_amount
        this.collecting_cycle = s.collecting_cycle
        this.no_contributed = s.get_no_contribution_members
        this.no_members = s.get_no_members
        this.contributed_amount = s.contributed_amount
        this.expected_amount = s.expected_amount
        this.current_collector = s.get_current_collector
        this.current_collect_position = s.current_collect_position
        this.collection_date = s.get_collection_date
        this.start_date = s.get_start_date
        this.collection_activated = s.collection_activated

        // 🔥 CORRECTION : toujours recalculer la date_history après avoir le cycle
        this.setDefaultDate()

        const histData = response.data[2]
        if (histData.pending) {
          this.groupPending = true
          this.pendingMessage = histData.pending_message
          this.njangi_members = []
          this.history_contributed_amount = 0
          this.history_expected_amount = 0
          this.customPeriodInfo = null
        } else {
          this.groupPending = false
          this.customPeriodInfo = histData.period_info || null
          const ts = Date.now()
          this.njangi_members = histData.group_members_contrib_info.map((m, i) => ({
            ...m,
            _key: `${m.membership_id}-${ts}-${i}`,
            contributed: Boolean(m.contributed),
            has_penalty: Boolean(m.has_penalty),
            penalty_amount: Number(m.penalty_amount || 0)
          }))
          this.history_contributed_amount = histData.total_contributed
          this.history_expected_amount = histData.expected_contribution
          this.lastUpdateTime = new Date().toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit', second: '2-digit'
          })
          await this.$nextTick()
        }
      } catch (error) {
        this.$store.commit('setSnackBarMessage', error.response ? `${error.response.status} ${error.response.statusText}` : 'Network error')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      }
      this.$store.commit('setIsLoading', false)
    },

    getDefaultDate() {
      const now = new Date()
      switch (this.collecting_cycle) {
        case 'Daily': case 'Custom': return now.toISOString().split('T')[0]
        case 'Weekly': return `${now.getFullYear()}-W${this.getNjangiWeekNumber(now).toString().padStart(2, '0')}`
        case 'Monthly': {
          const day = now.getDate()
          const tm = day >= 20 ? (now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2) : (now.getMonth() + 1)
          const ty = day >= 20 && now.getMonth() + 2 > 12 ? now.getFullYear() + 1 : now.getFullYear()
          return `${ty}-${tm.toString().padStart(2, '0')}`
        }
        default: return now.toISOString().split('T')[0]
      }
    }
  }
}
</script>

<style scoped>
/* ════════════════════════════════════════
   DESIGN TOKENS
════════════════════════════════════════ */
.ngd-root {
  --blue:       #1565c0;
  --blue-light: #e3f0ff;
  --blue-mid:   #1976d2;
  --green:      #2e7d32;
  --green-light:#e8f5e9;
  --red:        #c62828;
  --red-light:  #ffebee;
  --amber:      #e65100;
  --amber-light:#fff3e0;
  --grey-50:    #fafafa;
  --grey-100:   #f5f5f5;
  --grey-200:   #eeeeee;
  --grey-400:   #bdbdbd;
  --grey-600:   #757575;
  --grey-800:   #424242;
  --radius-sm:  8px;
  --radius-md:  12px;
  --radius-lg:  18px;
  --shadow-sm:  0 1px 4px rgba(0,0,0,.08);
  --shadow-md:  0 4px 16px rgba(0,0,0,.10);
  --shadow-lg:  0 8px 32px rgba(0,0,0,.13);

  font-family: 'Inter', 'Roboto', sans-serif;
  color: var(--grey-800);
  max-width: 860px;
  margin: 0 auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* PENDING BANNER */
.ngd-pending-banner {
  background: linear-gradient(135deg, #f57f17, #ff8f00);
  border-radius: var(--radius-md);
  padding: 14px 18px;
  box-shadow: var(--shadow-md);
}
.ngd-pending-inner { display: flex; align-items: center; gap: 14px; }
.ngd-pending-icon { font-size: 26px; flex-shrink: 0; }
.ngd-pending-text { color: white; font-size: 14px; line-height: 1.5; }
.ngd-pending-text strong { font-weight: 700; }

/* HEADER CARD */
.ngd-header-card {
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md), 0 8px 32px rgba(21,101,192,0.08);
  overflow: hidden;
  border: 1px solid #e3f0ff;
  position: relative;
}
.ngd-header-accent {
  height: 4px;
  background: linear-gradient(90deg, #1565c0, #42a5f5, #1565c0);
  width: 100%;
}
.ngd-header-hero {
  background: linear-gradient(135deg, #f8fbff 0%, #e3f0ff 100%);
  padding: 24px 24px 20px;
  position: relative;
  overflow: hidden;
}
.ngd-header-deco-circle {
  position: absolute;
  top: -30px; right: -30px;
  width: 120px; height: 120px;
  background: linear-gradient(135deg, rgba(21,101,192,0.06), rgba(66,165,245,0.1));
  border-radius: 50%;
  pointer-events: none;
}
.ngd-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  position: relative;
  z-index: 1;
}
.ngd-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ngd-group-avatar {
  width: 56px; height: 56px;
  background: linear-gradient(135deg, var(--blue), #1976d2);
  border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(21,101,192,0.35), 0 0 0 4px rgba(21,101,192,0.08);
}
.ngd-group-name {
  font-size: 22px; font-weight: 800;
  color: #0d47a1;
  line-height: 1.2; margin-bottom: 8px;
  letter-spacing: -0.3px;
}
.ngd-group-meta { display: flex; flex-wrap: wrap; gap: 8px; }
.ngd-cycle-badge, .ngd-status-badge {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 20px;
  font-size: 12px; font-weight: 700;
  letter-spacing: 0.3px; border: 1px solid transparent;
  transition: transform 0.15s;
}
.ngd-cycle-weekly  { background: rgba(21,101,192,0.1); color: #1565c0; border-color: rgba(21,101,192,0.15); }
.ngd-cycle-daily   { background: rgba(0,137,123,0.1);  color: #00695c; border-color: rgba(0,137,123,0.15); }
.ngd-cycle-monthly { background: rgba(194,24,91,0.1);  color: #c2185b; border-color: rgba(194,24,91,0.15); }
.ngd-cycle-custom  { background: rgba(56,142,60,0.1);  color: #388e3c; border-color: rgba(56,142,60,0.15); }
.ngd-status-active  { background: #e8f5e9; color: #2e7d32; border-color: #c8e6c9; }
.ngd-status-pending { background: #fff3e0; color: #e65100; border-color: #ffe0b2; }
.ngd-window-pill {
  display: inline-flex; align-items: center;
  padding: 6px 14px; border-radius: 24px;
  font-size: 12px; font-weight: 700;
  flex-shrink: 0; white-space: nowrap;
  border: 1.5px solid transparent;
  transition: transform 0.15s, box-shadow 0.15s;
}
.ngd-window-open {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  color: #1b5e20; border-color: #a5d6a7;
  box-shadow: 0 2px 8px rgba(46,125,50,0.15);
}
.ngd-window-closed {
  background: linear-gradient(135deg, #ffebee, #ffcdd2);
  color: #b71c1c; border-color: #ef9a9a;
  box-shadow: 0 2px 8px rgba(198,40,40,0.12);
}
.ngd-description {
  font-size: 13.5px; color: #546e7a;
  margin: 14px 0 0; line-height: 1.6;
  font-weight: 500; position: relative; z-index: 1;
}

/* Info grid */
.ngd-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px; padding: 20px 24px; background: #fff;
}
.ngd-info-item {
  display: flex; flex-direction: column; gap: 4px;
  background: #fafafa; border-radius: 12px; padding: 14px 16px;
  border: 1px solid #f0f0f0; position: relative; overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}
.ngd-info-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  border-color: #e0e0e0;
}
.ngd-info-accent {
  position: absolute; top: 0; left: 0; width: 3px; height: 100%;
  border-radius: 0 2px 2px 0;
}
.ngd-info-item--contact .ngd-info-accent { background: #1565c0; }
.ngd-info-item--contrib .ngd-info-accent { background: #00897b; }
.ngd-info-item--window  .ngd-info-accent { background: #ff8f00; }
.ngd-info-item--custom  .ngd-info-accent { background: #7b1fa2; }
.ngd-info-item--start   .ngd-info-accent { background: #5c6bc0; }
.ngd-info-label {
  display: flex; align-items: center;
  font-size: 10px; color: #78909c; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.ngd-info-value {
  font-size: 14px; font-weight: 700; color: #263238;
}
.ngd-info-currency {
  font-size: 11px; color: #78909c; font-weight: 600; margin-left: 2px;
}
.ngd-info-sub {
  font-size: 11px; font-weight: 600; color: #78909c;
}
.ngd-info-sub--cycle { color: #00897b; }
.ngd-info-sub--window { color: #ff8f00; }

/* Responsive */
@media (max-width: 600px) {
  .ngd-header-hero { padding: 18px 16px 16px; }
  .ngd-info-grid { padding: 16px; grid-template-columns: 1fr 1fr; }
  .ngd-group-name { font-size: 18px; }
  .ngd-header-top { flex-direction: column; gap: 10px; }
  .ngd-window-pill { align-self: flex-start; }
  /* MODIF : admin contact sur une ligne pleine largeur en mobile */
  .ngd-info-item--contact {
    grid-column: 1 / -1;
  }
}

/* PENALTY CARD */
.ngd-penalty-card {
  background: white; border-radius: var(--radius-md);
  overflow: hidden; box-shadow: var(--shadow-md);
  border: 1.5px solid #ef9a9a;
}
.ngd-penalty-header {
  background: linear-gradient(135deg, #c62828, #b71c1c);
  color: white; padding: 12px 18px;
  display: flex; align-items: center;
  font-size: 15px; font-weight: 700;
}
.ngd-penalty-body { padding: 16px 18px; }
.ngd-penalty-amounts {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 12px; flex-wrap: wrap;
}
.ngd-penalty-amt-item { text-align: center; }
.ngd-penalty-amt-label { font-size: 11px; color: var(--grey-600); text-transform: uppercase; letter-spacing: .3px; margin-bottom: 2px; }
.ngd-penalty-amt-val { font-size: 22px; font-weight: 800; line-height: 1; }
.ngd-penalty-amt-val.missed   { color: var(--grey-800); }
.ngd-penalty-amt-val.penalty  { color: #e65100; }
.ngd-penalty-amt-val.total    { color: var(--red); }
.ngd-penalty-amt-cur { font-size: 11px; color: var(--grey-600); }
.ngd-penalty-divider { font-size: 20px; font-weight: 300; color: var(--grey-400); padding: 0 4px; }
.ngd-penalty-note { font-size: 12px; color: var(--grey-600); margin: 0; display: flex; align-items: flex-start; gap: 4px; line-height: 1.5; }

/* ACTION ROW */
.ngd-action-row {
  display: flex; gap: 10px; flex-wrap: wrap; justify-content: flex-end;
}
.ngd-btn {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 10px 20px; border-radius: 24px;
  font-size: 13px; font-weight: 700; letter-spacing: .3px;
  cursor: pointer; border: none; text-decoration: none;
  transition: transform .15s, box-shadow .15s, opacity .15s;
  white-space: nowrap;
}
.ngd-btn:not(.ngd-btn-disabled):hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
.ngd-btn-disabled { opacity: .55; cursor: default; }
.ngd-btn-contribute {
  background: linear-gradient(135deg, #00897b, #00695c);
  color: white; box-shadow: 0 3px 10px rgba(0,137,123,.35);
}
.ngd-btn-penalty {
  background: linear-gradient(135deg, #e65100, #bf360c);
  color: white; box-shadow: 0 3px 10px rgba(230,81,0,.35);
}
.ngd-btn-blocked {
  background: var(--red-light); color: var(--red); border: 1.5px solid #ef9a9a;
}
.ngd-btn-window-closed {
  background: var(--grey-100); color: var(--grey-600); border: 1.5px solid var(--grey-200);
}
@keyframes pulse-glow {
  0%,100% { box-shadow: 0 3px 10px rgba(0,137,123,.35); }
  50% { box-shadow: 0 3px 22px rgba(0,137,123,.65); }
}
.ngd-btn-pulse { animation: pulse-glow 2s ease-in-out infinite; }

/* STATS CARD */
.ngd-stats-card {
  background: white; border-radius: var(--radius-lg);
  padding: 20px; box-shadow: var(--shadow-md);
}
.ngd-stats-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 12px; margin-bottom: 18px;
}
.ngd-stats-period-label {
  font-size: 15px; font-weight: 700; color: var(--grey-800);
  display: flex; align-items: center;
}
.ngd-stats-period-range { font-size: 12px; color: var(--grey-600); margin-top: 3px; }
.ngd-collector-badge {
  display: inline-flex; align-items: center;
  background: linear-gradient(135deg, #ff8f00, #e65100);
  color: white; padding: 5px 12px; border-radius: 20px;
  font-size: 12px; font-weight: 600; flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(230,81,0,.3);
}
.ngd-collector-pos {
  margin-left: 6px; background: rgba(255,255,255,.25);
  border-radius: 10px; padding: 1px 6px;
  font-size: 10px; font-weight: 800;
}
.ngd-stats-body {}
.ngd-progress-section {
  display: flex; align-items: center; gap: 20px; margin-bottom: 14px;
}
.ngd-ring-wrap { position: relative; width: 80px; height: 80px; flex-shrink: 0; }
.ngd-ring { width: 80px; height: 80px; transform: rotate(-90deg); }
.ngd-ring-bg { fill: none; stroke: var(--grey-200); stroke-width: 8; }
.ngd-ring-fill { fill: none; stroke-width: 8; stroke-linecap: round; transition: stroke-dashoffset .6s ease, stroke .4s; }
.ngd-ring-label {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  font-size: 15px; font-weight: 800; color: var(--grey-800);
}
.ngd-progress-meta { display: flex; flex-direction: column; gap: 6px; }
.ngd-progress-row { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.ngd-prog-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.ngd-dot-success { background: #43a047; }
.ngd-dot-error   { background: #e53935; }
.ngd-progress-total { font-size: 13px; font-weight: 700; color: var(--grey-800); margin-top: 4px; padding-top: 6px; border-top: 1px solid var(--grey-200); }
.ngd-slash { color: var(--grey-400); margin: 0 2px; }
.ngd-bar-wrap { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.ngd-bar-track { flex: 1; height: 10px; background: var(--grey-200); border-radius: 5px; overflow: hidden; }
.ngd-bar-fill { height: 100%; background: linear-gradient(90deg, #1565c0, #1e88e5); border-radius: 5px; transition: width .5s ease; }
.ngd-bar-complete { background: linear-gradient(90deg, #2e7d32, #43a047); }
.ngd-bar-pct { font-size: 12px; font-weight: 700; min-width: 36px; text-align: right; }
.ngd-collection-date { font-size: 12px; color: var(--grey-600); margin-top: 4px; }

/* TRANSACTIONS */
.ngd-transactions { background: white; border-radius: var(--radius-md); padding: 16px; box-shadow: var(--shadow-sm); }
.ngd-section-title {
  display: flex; align-items: center;
  font-size: 14px; font-weight: 700; color: var(--grey-800);
  margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid var(--grey-200);
}
.ngd-txn-list { display: flex; flex-direction: column; gap: 8px; }
.ngd-txn-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px; background: var(--grey-50); border-radius: var(--radius-sm);
}
.ngd-txn-left { display: flex; align-items: center; gap: 12px; }
.ngd-txn-icon {
  width: 34px; height: 34px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ngd-txn-in  { background: var(--blue); }
.ngd-txn-out { background: var(--red); }
.ngd-txn-type { font-size: 12px; color: var(--grey-600); font-weight: 500; }
.ngd-txn-amount { font-size: 14px; font-weight: 700; }
.ngd-txn-right { text-align: right; }
.ngd-txn-status { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px; display: inline-block; }
.ngd-txn-confirmed { background: var(--green-light); color: var(--green); }
.ngd-txn-pending   { background: var(--blue-light); color: var(--blue); }
.ngd-txn-date { font-size: 11px; color: var(--grey-600); margin-top: 3px; }

/* HISTORY CARD */
.ngd-history-card {
  background: white; border-radius: var(--radius-lg);
  padding: 20px; box-shadow: var(--shadow-md);
}
.ngd-history-pending {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px 20px; gap: 12px;
}
.ngd-history-pending-text { color: var(--grey-600); font-size: 14px; text-align: center; max-width: 340px; margin: 0; }
.ngd-period-selector {
  background: var(--grey-50); border-radius: var(--radius-md);
  padding: 14px 16px; margin-bottom: 16px;
  border: 1px solid var(--grey-200);
}
.ngd-period-label {
  font-size: 12px; font-weight: 700; color: var(--grey-600);
  text-transform: uppercase; letter-spacing: .4px;
  display: flex; align-items: center; margin-bottom: 10px;
}
.ngd-period-controls { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ngd-date-input-wrap {
  display: flex; align-items: center; gap: 8px;
  background: white; border: 1.5px solid var(--grey-200);
  border-radius: var(--radius-sm); padding: 8px 12px;
  cursor: pointer; flex: 1; min-width: 180px;
  transition: border-color .15s;
}
.ngd-date-input-wrap:hover { border-color: var(--blue); }
.ngd-date-icon, .ngd-date-caret { color: var(--grey-600); flex-shrink: 0; }
.ngd-date-input {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: var(--grey-800); cursor: pointer;
}
.ngd-select-row { display: flex; gap: 8px; flex-wrap: wrap; }
.ngd-select-wrap {
  display: flex; align-items: center; gap: 6px;
  background: white; border: 1.5px solid var(--grey-200);
  border-radius: var(--radius-sm); padding: 6px 10px;
  transition: border-color .15s;
}
.ngd-select-wrap:focus-within { border-color: var(--blue); }
.ngd-select {
  border: none; outline: none; background: transparent;
  font-size: 14px; color: var(--grey-800); cursor: pointer;
  min-width: 110px;
}
.ngd-refresh-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--blue); color: white;
  border: none; border-radius: var(--radius-sm);
  padding: 8px 16px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: opacity .15s, transform .15s;
  white-space: nowrap;
}
.ngd-refresh-btn:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
.ngd-refresh-btn:disabled { opacity: .6; cursor: default; }
.ngd-refresh-label { display: none; }
@media (min-width: 500px) { .ngd-refresh-label { display: inline; } }
@keyframes spin { to { transform: rotate(360deg); } }
.ngd-spin { animation: spin 1s linear infinite; }
.ngd-date-ctx {
  display: inline-flex; align-items: center;
  margin-top: 10px; font-size: 12px; color: var(--blue);
  background: var(--blue-light); padding: 4px 10px; border-radius: 12px;
  font-weight: 600;
}

/* History bar */
.ngd-hist-bar-wrap { margin-bottom: 8px; }
.ngd-hist-bar-track { height: 8px; background: var(--grey-200); border-radius: 4px; overflow: hidden; margin-bottom: 6px; }
.ngd-hist-bar-fill { height: 100%; background: linear-gradient(90deg, #1565c0, #1e88e5); border-radius: 4px; transition: width .5s ease; }
.ngd-hist-bar-full { background: linear-gradient(90deg, #2e7d32, #43a047); }
.ngd-hist-bar-meta { display: flex; justify-content: space-between; font-size: 12px; color: var(--grey-600); }
.ngd-hist-pct { font-weight: 700; color: var(--grey-800); }
.ngd-last-update {
  font-size: 11px; color: var(--grey-400);
  text-align: right; margin-bottom: 14px; margin-top: 4px;
  display: flex; align-items: center; justify-content: flex-end;
}

/* Member list */
.ngd-member-list { display: flex; flex-direction: column; gap: 8px; }
.ngd-member-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; border-radius: var(--radius-md);
  border-left: 4px solid transparent;
  transition: transform .15s, box-shadow .15s;
}
.ngd-member-row:hover { transform: translateY(-1px); box-shadow: var(--shadow-sm); }
.ngd-member-ok  { background: #f1f8f4; border-left-color: #43a047; }
.ngd-member-nok { background: #fff5f5; border-left-color: #e53935; }
.ngd-member-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ngd-avatar-ok  { background: #43a047; }
.ngd-avatar-nok { background: #e53935; }
.ngd-member-info { flex: 1; min-width: 0; }
.ngd-member-name {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  font-size: 14px; font-weight: 600; margin-bottom: 4px;
}
.ngd-position-tag {
  background: var(--blue); color: white;
  font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 10px;
}
.ngd-member-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.ngd-tag {
  display: inline-flex; align-items: center;
  font-size: 11px; font-weight: 600;
  padding: 2px 8px; border-radius: 10px;
}
.ngd-tag-ok      { background: var(--green-light); color: var(--green); }
.ngd-tag-nok     { background: var(--red-light); color: var(--red); }
.ngd-tag-penalty { background: var(--amber-light); color: var(--amber); }

/* Empty state */
.ngd-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px 20px; gap: 10px;
}
.ngd-empty p { color: var(--grey-600); font-size: 13px; margin: 0; }

/* DIALOG */
.ngd-dialog {
  background: white; border-radius: var(--radius-lg); overflow: hidden;
  display: flex; flex-direction: column;
  max-height: 90vh;
}
.ngd-dialog-header {
  background: linear-gradient(135deg, #c62828, #b71c1c);
  color: white; padding: 16px 20px;
  display: flex; align-items: center;
  font-size: 16px; font-weight: 700;
  position: relative;
}
.ngd-dialog-close {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,.15); border: none; border-radius: 50%;
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background .15s;
}
.ngd-dialog-close:hover { background: rgba(255,255,255,.3); }
.ngd-dialog-body { flex: 1; overflow-y: auto; padding: 20px; }
.ngd-dialog-totals {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  padding: 16px; background: var(--grey-50); border-radius: var(--radius-md);
  margin-bottom: 20px; flex-wrap: wrap;
}
.ngd-dialog-total-item { text-align: center; padding: 0 8px; }
.ngd-total-highlight { background: var(--red-light); border-radius: var(--radius-sm); padding: 8px 16px; }
.ngd-dialog-total-label { font-size: 11px; color: var(--grey-600); text-transform: uppercase; letter-spacing: .3px; margin-bottom: 3px; }
.ngd-dialog-total-val { font-size: 24px; font-weight: 800; line-height: 1; }
.ngd-dialog-total-val.missed  { color: var(--grey-800); }
.ngd-dialog-total-val.penalty { color: #e65100; }
.ngd-dialog-total-val.total   { color: var(--red); }
.ngd-dialog-total-cur { font-size: 11px; color: var(--grey-600); }
.ngd-plus { font-size: 22px; color: var(--grey-400); font-weight: 300; }
.ngd-dialog-section-label {
  display: flex; align-items: center;
  font-size: 13px; font-weight: 700; color: var(--grey-800);
  margin-bottom: 10px; padding-bottom: 6px;
  border-bottom: 1px solid var(--grey-200);
}
.ngd-breakdown-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
.ngd-breakdown-row {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 12px; background: var(--grey-50);
  border-radius: var(--radius-sm); border-left: 3px solid #ef9a9a;
}
.ngd-breakdown-num {
  width: 26px; height: 26px; flex-shrink: 0;
  background: var(--red); color: white;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
}
.ngd-breakdown-body { flex: 1; min-width: 0; }
.ngd-breakdown-period { font-size: 12px; font-weight: 600; color: var(--grey-800); margin-bottom: 8px; line-height: 1.4; }
.ngd-breakdown-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ngd-bchip {
  font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 10px;
  display: inline-flex; align-items: center;
}
.ngd-bchip-missed   { background: #ffebee; color: #c62828; }
.ngd-bchip-penalty  { background: #fff3e0; color: #e65100; }
.ngd-bchip-total    { background: #fce4ec; color: #880e4f; font-weight: 700; }
.ngd-phone-input-wrap {
  display: flex; align-items: center; gap: 10px;
  border: 1.5px solid var(--grey-200); border-radius: var(--radius-sm);
  padding: 10px 14px; background: white;
  margin-bottom: 16px; transition: border-color .15s;
}
.ngd-phone-input-wrap:focus-within { border-color: var(--blue); }
.ngd-phone-icon { color: var(--grey-600); flex-shrink: 0; }
.ngd-phone-input {
  flex: 1; border: none; outline: none; font-size: 15px;
  background: transparent; color: var(--grey-800);
}
.ngd-operator-row { display: flex; gap: 12px; margin-bottom: 16px; }
.ngd-operator-card {
  flex: 1; border: 2px solid var(--grey-200); border-radius: var(--radius-md);
  padding: 14px 12px; cursor: pointer; text-align: center;
  transition: border-color .2s, transform .15s, box-shadow .15s; background: white;
}
.ngd-operator-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-md); }
.ngd-operator-selected-momo { border-color: #FFC107; background: linear-gradient(135deg, #fffde7, #fff9c4); box-shadow: 0 4px 16px rgba(255,193,7,.25); }
.ngd-operator-selected-om   { border-color: #FF9800; background: linear-gradient(135deg, #fff3e0, #ffe0b2); box-shadow: 0 4px 16px rgba(255,152,0,.25); }
.ngd-operator-name { font-size: 12px; font-weight: 600; color: var(--grey-800); margin-top: 4px; }
.ngd-info-note {
  background: var(--blue-light); color: var(--blue);
  border-radius: var(--radius-sm); padding: 8px 12px;
  font-size: 12px; display: flex; align-items: center;
}
.ngd-dialog-footer {
  padding: 14px 20px;
  display: flex; justify-content: space-between; align-items: center;
  border-top: 1px solid var(--grey-200);
  gap: 10px;
}
.ngd-btn-cancel {
  background: transparent; color: var(--grey-600);
  border: 1.5px solid var(--grey-200);
  padding: 9px 20px; border-radius: 24px;
  font-size: 13px; font-weight: 600; cursor: pointer;
  transition: background .15s;
}
.ngd-btn-cancel:hover { background: var(--grey-100); }
.ngd-btn-pay {
  background: linear-gradient(135deg, #c62828, #b71c1c);
  color: white; border: none;
  padding: 10px 24px; border-radius: 24px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  box-shadow: 0 3px 12px rgba(198,40,40,.4);
  display: inline-flex; align-items: center;
  transition: opacity .15s, transform .15s;
}
.ngd-btn-pay:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
.ngd-btn-pay:disabled { opacity: .55; cursor: default; }

/* TRANSITIONS */
.fade-slide-enter-active { transition: all .35s ease; }
.fade-slide-leave-active { transition: all .25s ease; }
.fade-slide-enter, .fade-slide-leave-to { opacity: 0; transform: translateY(-8px); }
.member-list-enter-active, .member-list-leave-active { transition: all .3s ease; }
.member-list-enter, .member-list-leave-to { opacity: 0; transform: translateX(-16px); }
.member-list-move { transition: transform .35s ease; }

/* TOOLTIP */
.ngd-tooltip {
  background: rgba(15,23,42,.93) !important;
  color: #f1f5f9 !important;
  border-radius: 10px !important;
  font-size: 13px !important;
  padding: 10px 14px !important;
  max-width: 280px;
  line-height: 1.5;
}

/* RESPONSIVE */
@media (max-width: 600px) {
  .ngd-root { padding: 10px; gap: 12px; }
  .ngd-header-card { padding: 14px; }
  .ngd-group-name { font-size: 17px; }
  .ngd-header-top { flex-direction: column; }
  .ngd-stats-header { flex-direction: column; gap: 8px; }
  .ngd-action-row { justify-content: stretch; }
  .ngd-btn { flex: 1; }
  .ngd-dialog-totals { flex-direction: column; gap: 6px; }
  .ngd-plus { display: none; }
  .ngd-dialog-total-item { flex-direction: row; gap: 8px; align-items: center; }
  .ngd-operator-row { gap: 8px; }
  .ngd-progress-section { flex-direction: column; text-align: center; }
  .ngd-progress-meta { align-items: center; }
  .ngd-info-item--contact { grid-column: 1 / -1; }
}
</style>
